import { memo, useEffect, useState } from "react"

import type { HelpCenterTab } from "@productlane/zero"
import { Button, Dialog, Input } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { NucleoFillIconPicker } from "@/components/nucleo-fill-icon-picker"
import { toast } from "@/components/toast"
import { generateId } from "@/lib/id"
import { mutateOk, useZero } from "@/lib/zero"

const DEFAULT_TAB_ICON = "BookOpen"

/** Create a docs tab, or edit one's name and icon. */
export const DocsTabDialog = memo(
	({
		isOpen,
		onClose,
		tab,
		portalInstanceId,
		nextOrder,
		onCreated,
	}: {
		isOpen: boolean
		onClose: () => void
		tab?: Pick<HelpCenterTab, "id" | "name" | "icon"> | null
		portalInstanceId: string | null
		nextOrder: number
		onCreated?: (tabId: string) => void
	}) => {
		const zero = useZero()
		const [name, setName] = useState(tab?.name ?? "")
		const [icon, setIcon] = useState<string>(tab?.icon ?? DEFAULT_TAB_ICON)
		const [isSaving, setIsSaving] = useState(false)

		useEffect(() => {
			if (!isOpen) return
			setName(tab?.name ?? "")
			setIcon(tab?.icon ?? DEFAULT_TAB_ICON)
		}, [isOpen, tab])

		const submit = async () => {
			const trimmed = name.trim()
			if (!trimmed) {
				toast.info({ title: "Give the tab a name" })
				return
			}
			setIsSaving(true)
			try {
				if (tab) {
					await mutateOk(
						zero.mutate(
							mutators.helpCenterTabs.update({
								id: tab.id,
								name: trimmed,
								icon,
							}),
						),
						"server",
					)
				} else {
					const id = generateId()
					await mutateOk(
						zero.mutate(
							mutators.helpCenterTabs.create({
								id,
								name: trimmed,
								icon,
								order: nextOrder,
								portalInstanceId,
							}),
						),
						"server",
					)
					onCreated?.(id)
				}
				onClose()
			} catch (error) {
				toast.error({
					title: tab ? "Couldn't save the tab" : "Couldn't create the tab",
					description:
						error instanceof Error ? error.message : "Try again in a moment.",
				})
			} finally {
				setIsSaving(false)
			}
		}

		return (
			<Dialog
				className="max-w-80 p-2"
				isOpen={isOpen}
				onOpenChange={(open) => {
					if (!open) onClose()
				}}
			>
				<h2 className="mx-2 mb-4 mt-1 text-lg font-bold">
					{tab ? "Edit tab" : "New tab"}
				</h2>
				<form
					className="flex flex-col gap-2 px-2 py-2"
					onSubmit={(e) => {
						e.preventDefault()
						void submit()
					}}
				>
					<div className="flex items-start gap-2">
						<NucleoFillIconPicker
							icon={icon}
							onChange={(next) => setIcon(next ?? DEFAULT_TAB_ICON)}
							buttonVariant="outline"
						/>
						<Input
							name="docs-tab-name"
							autoFocus
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Tab name"
							maxLength={40}
							className="w-full text-sm text-primary outline-none"
						/>
					</div>
					<Button
						type="submit"
						variant="outline"
						isLoading={isSaving}
						className="w-full rounded-md p-2"
					>
						{tab ? "Save" : "Create tab"}
					</Button>
				</form>
			</Dialog>
		)
	},
)
